<template>
  <div v-if="battery" class="forms-elements">
    <Breadcrumbs v-bind:breadcrumbs="breadcrumbs" />

    <Widget v-if="battery">
      <b-row>
        <b-col lg="6" md="6" sm="12">
          <legend>
            <strong>Basisdaten</strong>
          </legend>

          <b-form-group
            horizontal
            :label="$t('manufacturer')"
            label-for="manufacturer"
            label-class="text-md-right"
            :label-cols="4"
            breakpoint="md"
          >
            <b-form-input
              v-model="battery.manufacturer"
              type="text"
              :placeholder="$t('manufacturer')"
            />
          </b-form-group>
          <b-form-group
            horizontal
            :label="$t('type')"
            label-for="type"
            label-class="text-md-right"
            :label-cols="4"
            breakpoint="md"
          >
            <b-form-input v-model="battery.type" type="text" :placeholder="$t('type')" />
          </b-form-group>

          <DropzoneUpload
            v-if="!isNew"
            :complete="isAttachmentUploadComplete"
            :parentId="battery.id"
            :uploadCallback="uploadBatteryAttachment"
            v-on:dropzone-upload:reset="isAttachmentUploadComplete = false"
          />

          <AttachmentList
            :attachments="battery.attachments"
            v-on:attachment-list:delete="onDeleteBatteryAttachment"
          ></AttachmentList>
        </b-col>
        <b-col lg="6" md="6" sm="12">
          <legend>
            <strong>Parameter</strong>
          </legend>

          <b-form-group
            horizontal
            :label="$t('capacity_kwh')"
            label-for="capacity"
            label-class="text-md-right"
            :label-cols="4"
            breakpoint="md"
          >
            <InputNumber
              v-model="battery.capacity"
              mode="decimal"
              :minFractionDigits="3"
              suffix=" kWh"
              locale="de-DE"
              :placeholder="$t('capacity_kwh')"
            />
          </b-form-group>

          <b-form-group
            horizontal
            :label="$t('dischargePower')"
            label-for="dischargePower"
            label-class="text-md-right"
            :label-cols="4"
            breakpoint="md"
          >
            <InputNumber
              v-model="battery.dischargePower"
              mode="decimal"
              :minFractionDigits="3"
              suffix=" kW"
              locale="de-DE"
              :placeholder="$t('dischargePower')"
            />
          </b-form-group>

          <b-form-group
            horizontal
            :label="$t('batteryModuleCount')"
            label-for="batteryModuleCount"
            label-class="text-md-right"
            :label-cols="4"
            breakpoint="md"
          >
            <InputNumber
              v-model="battery.batteryModuleCount"
              mode="decimal"
              locale="de-DE"
              :placeholder="$t('batteryModuleCount')"
            />
          </b-form-group>

          <b-form-group
            horizontal
            :label="$t('batteryModuleCapacity')"
            label-for="batteryModuleCapacity"
            label-class="text-md-right"
            :label-cols="4"
            breakpoint="md"
          >
            <InputNumber
              v-model="battery.batteryModuleCapacity"
              mode="decimal"
              :minFractionDigits="3"
              suffix=" kWh"
              locale="de-DE"
              :placeholder="$t('batteryModuleCapacity')"
            />
          </b-form-group>

          <b-form-group
            horizontal
            :label="$t('usableCapacity')"
            label-for="usableCapacity"
            label-class="text-md-right"
            :label-cols="4"
            breakpoint="md"
          >
            <InputNumber
              v-model="battery.usableCapacity"
              mode="decimal"
              :minFractionDigits="3"
              suffix=" kWh"
              locale="de-DE"
              :placeholder="$t('usableCapacity')"
            />
          </b-form-group>

          <b-form-group
            horizontal
            :label="$t('ratedVoltage')"
            label-for="ratedVoltage"
            label-class="text-md-right"
            :label-cols="4"
            breakpoint="md"
          >
            <InputNumber
              v-model="battery.ratedVoltage"
              mode="decimal"
              :minFractionDigits="3"
              suffix=" V"
              locale="de-DE"
              :placeholder="$t('ratedVoltage')"
            />
          </b-form-group>

          <b-form-group
            horizontal
            :label="$t('connectionType')"
            label-for="connectionType"
            label-class="text-md-right"
            :label-cols="4"
            breakpoint="md"
          >
            <multiselect
              :placeholder="$t('connectionType')"
              v-model="battery.connectionType"
              :options="getBatteryConnectionTypes"
              :show-labels="false"
              :allow-empty="false"
            >
              <template slot="singleLabel" slot-scope="{ option }">{{ $t(option) }}</template>
              <template slot="option" slot-scope="{ option }">
                {{ $t(option) }}
              </template>
            </multiselect>
          </b-form-group>
        </b-col>
      </b-row>

      <b-row>
        <b-col lg="12" md="12" sm="12">
          <ButtonBar
            :showDeleteButton="buttons.showDeleteButton"
            :model="battery"
            modelEditRoute="BatteryEditPage"
            modelListRoute="BatteriesPage"
            modelRouteParamName="batteryNumber"
            :updateCallback="updateBattery"
            :createCallback="createBattery"
            :deleteCallback="deleteBattery"
            :fetchCallback="fetchBatteries"
          />
        </b-col>
      </b-row>
    </Widget>
  </div>
</template>

<script>
import Widget from '@/components/Widget/Widget';
import Breadcrumbs from '@/components/Breadcrumbs';
import { mapGetters, mapActions } from 'vuex';
import { yesNo } from '@/helpers/enums';
import DropzoneUpload from '@/components/DropzoneUpload';
import AttachmentList from '@/components/AttachmentList';
import InputNumber from 'primevue/inputnumber';
import Multiselect from 'vue-multiselect';
import ButtonBar from '@/components/ButtonBar.vue';

export default {
  name: 'BatteryEditPage',
  components: {
    Widget,
    Breadcrumbs,
    DropzoneUpload,
    AttachmentList,
    InputNumber,
    Multiselect,
    ButtonBar,
  },
  props: {
    batteryNumber: {
      required: false,
      default: null,
    },
  },
  data() {
    return {
      number: null,
      user: {},
      buttonsDisabled: false,
      isAttachmentUploadComplete: false,
    };
  },
  computed: {
    ...mapGetters(['getBattery', 'getBatteryByNumber', 'getBatteryConnectionTypes']),
    buttons() {
      return {
        showDeleteButton: this.battery && this.battery.number !== '_new' ? true : false,
      };
    },
    optionsYesNo() {
      return yesNo();
    },
    battery() {
      return this.getBatteryByNumber(this.number);
    },
    isNew() {
      return this.battery.id === '_new';
    },
    breadcrumbs() {
      return [
        { name: 'Home', route: { name: 'home' } },
        { name: this.$t('battery'), route: { name: 'BatteriesPage' } },
        { name: this.battery.number !== '_new' ? this.$t('edit') : this.$t('new') },
      ];
    },
  },
  methods: {
    ...mapActions([
      'initBattery',
      'fetchBatteries',
      'fetchBatteryByNumber',
      'updateBattery',
      'createBattery',
      'deleteBattery',
      'deleteBatteryAttachment',
      'uploadBatteryAttachment',
      'fetchBatteryConnectionTypes',
    ]),
    isDisabled(e) {
      e.preventDefault();
    },
    onDeleteBatteryAttachment(event) {
      this.deleteBatteryAttachment({
        batteryId: this.battery.id,
        attachmentId: event.attachmentId,
      });
    },
  },
  async mounted() {
    await this.fetchBatteryConnectionTypes();

    if (!this.battery && this.batteryNumber) {
      this.number = this.batteryNumber;
      this.fetchBatteryByNumber(this.number);
    } else {
      this.initBattery();
      this.number = '_new';
    }
    // after upload is complete emit event to clear dropzone preview
    this.$store.subscribeAction({
      after: (action, state) => {
        if (action.type === 'uploadBatteryAttachment') {
          this.isAttachmentUploadComplete = true;
        }
      },
    });
  },
};
</script>

<style scoped lang="scss">
.batteryContacts {
  text-align: right;
}
.trash-contact {
  cursor: pointer;
}
</style>
